import { PPQ, TICKS_PER_STEP } from '../music/compile';
import { STEPS_PER_BAR } from '../domain/schemas';

/**
 * Musical time conversions (Phase 4A). Pure functions over the project's actual time
 * model: one tempo per project (BPM) and 4/4 meter (16 sixteenth steps per bar). There is
 * no tempo automation or variable meter in SonicForge, so none is modelled here.
 *
 * Ticks (PPQ 192, 48 per sixteenth step) are the exact internal unit for musical
 * positions — the same unit the music compiler and the audio transport use. Seconds are
 * always computed from integer ticks (never accumulated), so long sessions cannot drift.
 * Frames are derived from seconds only where frame-based animation needs them.
 */
export { PPQ, TICKS_PER_STEP };
export const BEATS_PER_BAR = 4;
export const TICKS_PER_BEAT = PPQ;
export const TICKS_PER_BAR = PPQ * BEATS_PER_BAR;
/** Sanity check: the step grid and the bar agree (16 sixteenths per 4/4 bar). */
export const STEPS_PER_BAR_CHECK = TICKS_PER_BAR / TICKS_PER_STEP === STEPS_PER_BAR;

/** Matches `frameAtTime`'s epsilon so a frame boundary never floors one frame low. */
const FRAME_EPSILON = 1e-6;

export const secondsPerTick = (bpm: number): number => 60 / (bpm * PPQ);
export const ticksToSeconds = (ticks: number, bpm: number): number => (ticks * 60) / (bpm * PPQ);
/** Seconds → ticks (fractional; round for a musical position). */
export const secondsToTicks = (seconds: number, bpm: number): number => (seconds * bpm * PPQ) / 60;

export const ticksToBeats = (ticks: number): number => ticks / TICKS_PER_BEAT;
export const beatsToTicks = (beats: number): number => Math.round(beats * TICKS_PER_BEAT);
export const ticksToBars = (ticks: number): number => ticks / TICKS_PER_BAR;
export const barsToTicks = (bars: number): number => Math.round(bars * TICKS_PER_BAR);
export const ticksToSteps = (ticks: number): number => ticks / TICKS_PER_STEP;
export const stepsToTicks = (steps: number): number => Math.round(steps * TICKS_PER_STEP);

/** Displayed animation frame for a time (floor, like playback). */
export const secondsToFrame = (seconds: number, fps: number): number => Math.floor(Math.max(0, seconds) * fps + FRAME_EPSILON);
export const frameToSeconds = (frame: number, fps: number): number => frame / fps;
/** The frame on which a musical position is displayed. */
export const ticksToFrame = (ticks: number, bpm: number, fps: number): number => secondsToFrame(ticksToSeconds(ticks, bpm), fps);
/** First tick displayed on (or after the start of) a frame — rounded to the tick grid. */
export const frameToTicks = (frame: number, bpm: number, fps: number): number => Math.round(secondsToTicks(frame / fps, bpm));

/** Snaps ticks to a subdivision of the beat (1 = beats, 2 = eighths, 4 = sixteenths, 3 = triplets). */
export function quantizeTicks(ticks: number, subdivision: number): number {
  const grid = TICKS_PER_BEAT / subdivision;
  return Math.round(Math.round(ticks / grid) * grid);
}

export interface MusicalPosition {
  /** 1-based bar. */
  bar: number;
  /** 1-based beat in the bar. */
  beat: number;
  /** Ticks into the beat (0–191). */
  tick: number;
}

export function ticksToPosition(ticks: number): MusicalPosition {
  const t = Math.max(0, Math.round(ticks));
  return { bar: Math.floor(t / TICKS_PER_BAR) + 1, beat: Math.floor((t % TICKS_PER_BAR) / TICKS_PER_BEAT) + 1, tick: t % TICKS_PER_BEAT };
}

export const positionToTicks = (p: MusicalPosition): number => (p.bar - 1) * TICKS_PER_BAR + (p.beat - 1) * TICKS_PER_BEAT + p.tick;

/** "3.2.096" style label (bar.beat.tick, 1-based). */
export const formatPosition = (ticks: number): string => {
  const p = ticksToPosition(ticks);
  return `${p.bar}.${p.beat}.${String(p.tick).padStart(3, '0')}`;
};

export function formatSeconds(seconds: number): string {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(3).padStart(6, '0')}`;
}

/**
 * Position inside an audio clip's source file at a session time: the clip starts at
 * `startStep`, plays `offsetSec` onwards at `playbackRate`, looping `durationSec`.
 * Returns null outside the clip.
 */
export function clipSourcePosition(clip: { startStep: number; offsetSec: number; durationSec: number; playbackRate: number; loops: number }, seconds: number, bpm: number): number | null {
  const start = ticksToSeconds(clip.startStep * TICKS_PER_STEP, bpm);
  const into = (seconds - start) * clip.playbackRate;
  if (into < 0 || into >= clip.durationSec * clip.loops) return null;
  return clip.offsetSec + (into % clip.durationSec);
}

/** "3.2" (bar.beat), "3.2.096" (bar.beat.tick) or "12.5s" → seconds; null when not understood. */
export function parseTimeInput(text: string, bpm: number): number | null {
  const t = text.trim().toLowerCase();
  let m = t.match(/^(\d+(?:\.\d+)?) ?s(?:ec(?:onds?)?)?$/);
  if (m) return Number(m[1]);
  m = t.match(/^(\d+)(?:\.(\d+)(?:\.(\d+))?)?$/);
  if (!m) return null;
  const bar = Number(m[1]);
  const beat = m[2] ? Number(m[2]) : 1;
  const tick = m[3] ? Number(m[3]) : 0;
  if (bar < 1 || beat < 1 || beat > 4 || tick > 191) return null;
  return ticksToSeconds(positionToTicks({ bar, beat, tick }), bpm);
}

